import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';
import { sendNotification } from 'utils/helperMethods';

interface ImportEventsPayload {
    sourcePlanId: string;
    targetPlanId: string;
    promoIds: string[];
}

interface ImportedMapping {
    source_event_id?: string;
    new_event_id?: string;
    source_promo_id?: string;
    new_promo_id?: string;
}

interface ImportEventsResponse {
    status: string;
    imported_events: ImportedMapping[];
    imported_promos: ImportedMapping[];
    warnings: string[];
}

const executeImport = async (payload: {
    user_id: string;
    source_plan_id: string;
    target_plan_id: string;
    promo_ids: string[];
}): Promise<ImportEventsResponse> => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.importEvents, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        data: payload,
    });
    return response.data;
};

const useImportEventsMutation = ({ onSuccess }: { onSuccess?: (data: ImportEventsResponse) => void } = {}) => {
    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: ({ sourcePlanId, targetPlanId, promoIds }: ImportEventsPayload) => {
            const userId = localStorage.getItem('email') ?? '';
            return executeImport({
                user_id: userId,
                source_plan_id: sourcePlanId,
                target_plan_id: targetPlanId,
                promo_ids: promoIds,
            });
        },
        onSuccess: (data) => {
            queryClient.removeQueries({ queryKey: [...tpoGenKeys.all, 'planning-groups'] });
            queryClient.removeQueries({ queryKey: [...tpoGenKeys.all, 'plan-list'] });
            queryClient.invalidateQueries({ queryKey: ['calendarData'] });
            const promoCount = data.imported_promos?.length ?? 0;
            sendNotification('success', `Successfully imported ${promoCount} promo(s)`);
            if (data.warnings?.length) {
                sendNotification('info', data.warnings.join(' \u2022 '));
            }
            onSuccess?.(data);
        },
        onError: (error: any) => {
            const message =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.message ||
                'Failed to import events';
            sendNotification('error', message);
        },
    });

    return { mutate, isPending };
};

export default useImportEventsMutation;
